// Funzioni pure sul menu di navigazione (l'albero salvato al login: `server/menu/*.json` filtrato per ruolo). Niente DOM né
// store: le usano la sidebar e i breadcrumb, e si provano da sole.
export interface VoceMenu {
  key: string;
  label?: string;
  icon?: string;
  route?: string;
  parent_label?: string;
  visibility?: boolean;
  items?: VoceMenu[];
}

// Toglie le voci nascoste (`visibility: false`) e i gruppi rimasti senza figli. Non modifica l'originale.
export function normalizzaMenu(lista: VoceMenu[] | null | undefined): VoceMenu[] {
  return (lista ?? [])
    .filter((voce) => voce.visibility !== false)
    .map((voce) => (voce.items ? { ...voce, items: normalizzaMenu(voce.items) } : { ...voce }))
    .filter((voce) => !voce.items || voce.items.length > 0);
}

// Legge il menu dalla stringa JSON salvata (localStorage): mai un'eccezione, al peggio un menu vuoto.
export function leggiMenuSalvato(testo: string | null | undefined): VoceMenu[] {
  if (!testo) return [];
  try {
    const dati: unknown = JSON.parse(testo);
    return Array.isArray(dati) ? (dati as VoceMenu[]) : [];
  } catch {
    return [];
  }
}

export interface RisultatoRicerca {
  voce: VoceMenu;
  // gruppi genitori, dalla radice
  antenati: VoceMenu[];
}

// Cerca una voce per route a qualsiasi profondità.
export function trovaPerRoute(route: string, lista: VoceMenu[], antenati: VoceMenu[] = []): RisultatoRicerca | null {
  for (const voce of lista) {
    if (voce.route === route) return { voce, antenati };
    if (voce.items) {
      const trovata = trovaPerRoute(route, voce.items, [...antenati, voce]);
      if (trovata) return trovata;
    }
  }
  return null;
}

// Vero se la route attiva sta sotto la voce (a qualsiasi profondità).
export function haFiglioAttivo(voce: VoceMenu, routeAttiva: string): boolean {
  return voce.items?.some((figlio) => figlio.route === routeAttiva || haFiglioAttivo(figlio, routeAttiva)) ?? false;
}

// Filtra per testo (ricorsivo). Un gruppo il cui nome corrisponde mostra tutti i suoi figli; altrimenti solo quelli che corrispondono.
// `traduci` rende la chiave i18n della voce nel testo mostrato.
export function filtraMenu(lista: VoceMenu[], ricerca: string, traduci: (chiave: string) => string): VoceMenu[] {
  const testo = ricerca.toLowerCase().trim();
  if (!testo) return lista;
  const filtra = (voci: VoceMenu[]): VoceMenu[] =>
    voci.reduce<VoceMenu[]>((acc, voce) => {
      const corrisponde = traduci(voce.label ?? "").toLowerCase().includes(testo);
      if (!voce.items || voce.items.length === 0) {
        if (corrisponde) acc.push(voce);
        return acc;
      }
      const figli = filtra(voce.items);
      if (corrisponde || figli.length > 0) acc.push({ ...voce, items: corrisponde ? voce.items : figli });
      return acc;
    }, []);
  return filtra(lista);
}

// Elenco piatto delle voci con una route (le destinazioni raggiungibili).
export function vociNavigabili(lista: VoceMenu[]): VoceMenu[] {
  return lista.flatMap((voce) => [...(voce.route ? [voce] : []), ...(voce.items ? vociNavigabili(voce.items) : [])]);
}

// Chiavi dei gruppi da aprire perché contengono una voce che corrisponde alla ricerca (o una sua discendente).
export function gruppiDaAprire(lista: VoceMenu[], ricerca: string, traduci: (chiave: string) => string): string[] {
  const testo = ricerca.toLowerCase().trim();
  if (!testo) return [];
  const chiavi: string[] = [];
  const visita = (voci: VoceMenu[]): boolean => {
    let almenoUna = false;
    for (const voce of voci) {
      const proprio = traduci(voce.label ?? "").toLowerCase().includes(testo);
      const figlio = voce.items ? visita(voce.items) : false;
      if (voce.items && (proprio || figlio)) chiavi.push(voce.key);
      almenoUna = almenoUna || proprio || figlio;
    }
    return almenoUna;
  };
  visita(lista);
  return chiavi;
}
